import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { getContactDisplayName } from "../../helpers/contactDisplayName";

const useStyles = makeStyles(theme => ({
	ticket: {
		alignItems: "flex-start",
		border: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e2e8f0"}`,
		borderRadius: 8,
		margin: theme.spacing(0.5, 1),
		minHeight: 82,
		padding: theme.spacing(1.25, 1.25, 1.25, 2),
		position: "relative",
		transition: "background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease",
		"&:hover": {
			backgroundColor: theme.palette.type === "dark" ? "rgba(91, 115, 255, 0.12)" : "#f3f6ff",
			borderColor: theme.palette.type === "dark" ? "#4a5f8c" : "#c9d6f7",
		},
		"&.Mui-selected": {
			backgroundColor: theme.palette.type === "dark" ? "rgba(91, 115, 255, 0.2)" : "#eaf0ff",
			borderColor: "#5b73ff",
			boxShadow: "0 2px 8px rgba(49, 84, 245, 0.12)",
		},
		"&.Mui-selected:hover": {
			backgroundColor: theme.palette.type === "dark" ? "rgba(91, 115, 255, 0.26)" : "#eaf0ff",
		},
	},

	pendingTicket: {
		cursor: "unset",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		gap: theme.spacing(0.75),
		minWidth: 0,
	},
	contactIdentity: {
		flex: 1,
		minWidth: 0,
	},
	contactName: {
		fontSize: 13,
		fontWeight: 800,
	},
	queueName: {
		color: theme.palette.type === "dark" ? "#9aa5b8" : "#718096",
		display: "block",
		fontSize: 11,
		lineHeight: 1.2,
		marginTop: 2,
	},

	lastMessageTime: {
		color: theme.palette.type === "dark" ? "#9aa5b8" : "#718096",
		fontSize: 11,
		flexShrink: 0,
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 32,
		marginLeft: "auto",
	},

	contactLastMessage: {
		color: theme.palette.type === "dark" ? "#b8c1d2" : "#5c6a7e",
		flex: 1,
		fontSize: 12,
		minWidth: 0,
		paddingRight: 0,
	},

	newMessagesCount: {
		alignSelf: "flex-end",
		margin: theme.spacing(0.25, 0, 0, 0.5),
	},

	badgeStyle: {
		color: "white",
		backgroundColor: green[500],
	},

	acceptButton: {
		flexShrink: 0,
		alignSelf: "flex-start",
		marginLeft: theme.spacing(1),
		marginTop: theme.spacing(-1),
	},

	ticketQueueColor: {
		flex: "none",
		borderRadius: 4,
		width: "4px",
		height: "calc(100% - 16px)",
		position: "absolute",
		top: 8,
		left: 6,
	},

	connectionTag: {
		alignSelf: "flex-end",
		backgroundColor: theme.palette.type === "dark" ? "#263b62" : "#e8f0ff",
		borderRadius: 10,
		color: theme.palette.type === "dark" ? "#bfccff" : "#3154b5",
		fontSize: 10,
		fontWeight: 700,
		lineHeight: "18px",
		maxWidth: 80,
		overflow: "hidden",
		padding: theme.spacing(0, 0.75),
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	priorityBadge: {
		alignSelf: "flex-start",
		borderRadius: 10,
		color: "#fff",
		fontSize: 9,
		fontWeight: 600,
		padding: "2px 6px",
		textTransform: "uppercase",
	},
}));

const priorityColors = {
	normal: "#607d8b",
	high: "#f57c00",
	urgent: "#d32f2f",
};

const TicketListItem = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(`/tickets/${id}`);
	};

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	return (
		<React.Fragment key={ticket.id}>
			<ListItem
				dense
				button
				onClick={e => {
					if (ticket.status === "pending") return;
					handleSelectTicket(ticket.id);
				}}
				selected={ticketId && +ticketId === ticket.id}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
				})}
			>
				<Tooltip
					arrow
					placement="right"
					title={ticket.queue?.name || "Sem fila"}
				>
					<span
						style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
						className={classes.ticketQueueColor}
					></span>
				</Tooltip>
				<ListItemAvatar>
					<Avatar src={ticket?.contact?.profilePicUrl} style={{ width: 38, height: 38 }} />
				</ListItemAvatar>
				<ListItemText
					disableTypography
					primary={
						<span className={classes.contactNameWrapper}>
							<span className={classes.contactIdentity}>
								<Typography
									className={classes.contactName}
									noWrap
									component="span"
									color="textPrimary"
								>
									{getContactDisplayName(ticket.contact)}
								</Typography>
								<Typography className={classes.queueName} noWrap component="span">
									{ticket.queue?.name || "Sin cola"}
								</Typography>
							</span>
							<span
								className={classes.priorityBadge}
								style={{ backgroundColor: priorityColors[ticket.priority] || priorityColors.normal }}
							>
								{i18n.t(`tickets.priorities.${ticket.priority || "normal"}`)}
							</span>
							{ticket.status === "closed" && (
								<Badge
									className={classes.closedBadge}
									badgeContent={"closed"}
									color="primary"
								/>
							)}
							{ticket.lastMessage && (
								<Typography
									className={classes.lastMessageTime}
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
										<>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
									) : (
										<>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
									)}
								</Typography>
							)}
						</span>
					}
					secondary={
						<span className={classes.contactNameWrapper}>
							<Typography
								className={classes.contactLastMessage}
								noWrap
								component="span"
								variant="body2"
								color="textSecondary"
							>
								{ticket.lastMessage ? (
									<MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
								) : (
									<br />
								)}
							</Typography>

							<Badge
								className={classes.newMessagesCount}
								badgeContent={ticket.unreadMessages}
								classes={{
									badge: classes.badgeStyle,
								}}
							/>
							{ticket.whatsappId && (
								<span className={classes.connectionTag} title={i18n.t("ticketsList.connectionTitle")}>
									{ticket.whatsapp?.name}
								</span>
							)}
						</span>
					}
				/>
				{ticket.status === "pending" && (
					<ButtonWithSpinner
						color="primary"
						variant="contained"
						className={classes.acceptButton}
						size="small"
						loading={loading}
						onClick={e => handleAcepptTicket(ticket.id)}
					>
						{i18n.t("ticketsList.buttons.accept")}
					</ButtonWithSpinner>
				)}
			</ListItem>
			<Divider variant="inset" component="li" />
		</React.Fragment>
	);
};

export default TicketListItem;
